import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, LogOut, UserPlus, Check, Trash2, KeyRound, Type } from "lucide-react";
import { AppFrame } from "@/components/AppFrame";
import { api } from "@/lib/api";
import { isLargeFont, setLargeFont } from "@/hooks/useLargeFont";
import { useAuth } from "@/context/AuthContext";
import { useProfile } from "@/context/ProfileContext";
import { Input } from "@/components/ui/input";
import {
  NOTIFY_KEY,
  enableReminders,
  disableReminders,
  notificationState,
  testReminder,
} from "@/hooks/useReminders";

const AccessPill = ({ value, onChange, testid }) => (
  <button
    data-testid={testid}
    onClick={() => onChange(value === "edit" ? "view" : "edit")}
    className="flex items-center gap-2 rounded-full border border-slate-700 px-3 py-2 text-[13px] font-medium text-slate-300"
  >
    <span
      data-testid={`${testid}-box`}
      className={`flex h-5 w-5 items-center justify-center rounded-md border transition-colors duration-200 ${
        value === "edit" ? "border-sky-400 bg-sky-500/25 text-sky-200" : "border-slate-600 text-transparent"
      }`}
    >
      <Check className="h-3.5 w-3.5" />
    </span>
    Edit access
  </button>
);

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { reloadProfiles } = useProfile();
  const [notify, setNotify] = useState(localStorage.getItem(NOTIFY_KEY) === "on");
  const [bigFont, setBigFont] = useState(isLargeFont);
  const [permission, setPermission] = useState(notificationState());
  const [family, setFamily] = useState({ shared_by_me: [], shared_with_me: [], received: [] });
  const [email, setEmail] = useState("");
  const [access, setAccess] = useState("edit");
  const [hasPassword, setHasPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    api
      .get("/auth/has-password")
      .then(({ data }) => setHasPassword(data.has_password))
      .catch(() => setHasPassword(false));
  }, []);

  const savePassword = async () => {
    try {
      await api.post("/auth/set-password", { password: newPassword });
      setNewPassword("");
      setHasPassword(true);
      toast.success("Password saved — you can now sign in with your email");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the password");
    }
  };

  const loadFamily = async () => {
    try {
      const { data } = await api.get("/family");
      setFamily(data);
    } catch {
      toast.error("Could not load family access");
    }
  };

  useEffect(() => {
    loadFamily();
  }, []);

  const toggleNotify = async () => {
    if (notify) {
      disableReminders();
      setNotify(false);
      return;
    }
    const res = await enableReminders();
    setPermission(notificationState());
    if (res === "granted") {
      setNotify(true);
      testReminder();
      toast.success("Reminders on");
    } else if (res === "denied") {
      toast.error("Blocked — allow notifications for this site in your browser settings");
    } else {
      toast.error("Notifications are not available in this browser");
    }
  };

  const invite = async () => {
    try {
      await api.post("/family/invite", { email, access });
      setEmail("");
      toast.success("Access shared — they need to accept it");
      loadFamily();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share access");
    }
  };

  const changeAccess = async (link_id, value) => {
    setFamily((f) => ({
      ...f,
      shared_by_me: f.shared_by_me.map((x) => (x.link_id === link_id ? { ...x, access: value } : x)),
    }));
    try {
      await api.patch(`/family/${link_id}`, { access: value });
      reloadProfiles();
    } catch {
      toast.error("Could not change access");
      loadFamily();
    }
  };

  const accept = async (link_id) => {
    try {
      await api.post("/family/accept", { link_id });
      toast.success("Access accepted");
      await loadFamily();
      await reloadProfiles();
    } catch {
      toast.error("Could not accept access");
    }
  };

  const removeLink = async (link_id) => {
    try {
      await api.delete(`/family/${link_id}`);
      await loadFamily();
      await reloadProfiles();
    } catch {
      toast.error("Could not remove access");
    }
  };

  return (
    <AppFrame title="Settings">
      <div className="space-y-3 pb-6">
        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5">
          <p className="text-[13px] text-slate-500">Signed in as</p>
          <p className="mt-1 text-[17px] font-semibold" data-testid="settings-user-name">
            {user?.name}
          </p>
          <p className="text-[13px] text-slate-500">{user?.email}</p>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold">Reminders</p>
              <p className="mt-0.5 text-[13px] text-slate-500">
                Get a notification when a dose is due.
              </p>
            </div>
            <button
              data-testid="toggle-notifications-btn"
              onClick={toggleNotify}
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 transition-colors duration-200 ${
                notify ? "bg-sky-500/15 text-sky-300 ring-sky-500/40" : "bg-slate-800/60 text-slate-500 ring-slate-700"
              }`}
            >
              {notify ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
            </button>
          </div>
          {permission === "denied" && (
            <p className="mt-2.5 text-[13px] text-amber-300/90" data-testid="notify-blocked-help">
              Blocked by the browser. On Android Chrome: tap the lock icon next to the address bar →
              Permissions → Notifications → Allow, then turn this on again.
            </p>
          )}
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5" data-testid="large-font-section">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[17px] font-semibold">Large Font</p>
              <p className="mt-0.5 text-[13px] text-slate-500">
                Bigger text and roomier buttons on the Day screen, for easier reading.
              </p>
            </div>
            <button
              data-testid="toggle-large-font-btn"
              onClick={() => {
                setLargeFont(!bigFont);
                setBigFont(!bigFont);
                toast.success(bigFont ? "Large Font off" : "Large Font on");
              }}
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-1 transition-colors duration-200 ${
                bigFont ? "bg-sky-500/15 text-sky-300 ring-sky-500/40" : "bg-slate-800/60 text-slate-500 ring-slate-700"
              }`}
            >
              <Type className={bigFont ? "h-6 w-6" : "h-5 w-5"} />
            </button>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5" data-testid="password-section">
          <p className="text-[17px] font-semibold">Password</p>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {hasPassword
              ? "Update the password you use to sign in with your email."
              : "Set a password so you can sign in with your email as well as Google."}
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              data-testid="set-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              className="h-12 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
            />
            <button
              data-testid="set-password-btn"
              onClick={savePassword}
              className="flex h-12 shrink-0 items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-4 text-[15px] font-semibold text-white active:scale-95"
            >
              <KeyRound className="h-4 w-4" /> Save
            </button>
          </div>
        </section>


        <section className="rounded-2xl border border-slate-800 bg-slate-900/45 p-3.5" data-testid="family-section">
          <p className="text-[17px] font-semibold">Family access</p>
          <p className="mt-0.5 text-[13px] text-slate-500">
            Share your medicines with another account. Sharing is one way — they only see yours unless
            they share theirs with you.
          </p>

          <div className="mt-3 space-y-2">
            <Input
              data-testid="family-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="family@gmail.com"
              className="h-12 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
            />
            <div className="flex items-center gap-2">
              <AccessPill value={access} onChange={setAccess} testid="invite-access" />
              <button
                data-testid="family-invite-btn"
                onClick={invite}
                className="ml-auto flex h-12 items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-4 text-[15px] font-semibold text-white active:scale-95"
              >
                <UserPlus className="h-4 w-4" /> Share
              </button>
            </div>
          </div>

          {family.received.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="family-received">
              <p className="text-[13px] text-sky-300">Shared with you — accept to view</p>
              {family.received.map((f) => (
                <div
                  key={f.link_id}
                  data-testid={`family-received-${f.link_id}`}
                  className="flex items-center gap-2 rounded-xl border border-sky-500/25 bg-sky-500/[0.07] px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] text-slate-200">{f.name || f.email}</p>
                    <p className="text-[13px] text-slate-500">{f.access === "view" ? "View only" : "Can edit"}</p>
                  </div>
                  <button
                    data-testid={`family-accept-${f.link_id}`}
                    onClick={() => accept(f.link_id)}
                    className="flex h-10 items-center gap-1 rounded-full bg-emerald-500/15 px-3 text-[13px] font-semibold text-emerald-300 active:scale-95"
                  >
                    <Check className="h-4 w-4" /> Accept
                  </button>
                  <button
                    data-testid={`family-decline-${f.link_id}`}
                    onClick={() => removeLink(f.link_id)}
                    className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {family.shared_by_me.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="shared-by-me-list">
              <p className="text-[13px] text-slate-500">You shared your account with</p>
              {family.shared_by_me.map((f) => (
                <div
                  key={f.link_id}
                  data-testid={`shared-by-me-${f.link_id}`}
                  className="rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2.5"
                >
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] text-slate-200">{f.name || f.email}</p>
                      <p className="truncate text-[13px] text-slate-500">
                        {f.status === "accepted" ? "Accepted" : "Waiting to accept"}
                      </p>
                    </div>
                    <button
                      data-testid={`family-remove-${f.link_id}`}
                      onClick={() => removeLink(f.link_id)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2">
                    <AccessPill
                      value={f.access}
                      onChange={(v) => changeAccess(f.link_id, v)}
                      testid={`access-${f.link_id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {family.shared_with_me.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="shared-with-me-list">
              <p className="text-[13px] text-slate-500">Accounts you can open</p>
              {family.shared_with_me.map((f) => (
                <div
                  key={f.link_id}
                  data-testid={`shared-with-me-${f.link_id}`}
                  className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] text-slate-200">{f.name || f.email}</p>
                    <p className="text-[13px] text-slate-500">{f.access === "view" ? "View only" : "Can edit"}</p>
                  </div>
                  <button
                    data-testid={`family-leave-${f.link_id}`}
                    onClick={() => removeLink(f.link_id)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        <button
          data-testid="logout-btn"
          onClick={logout}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-slate-800 text-[15px] font-semibold text-slate-400 transition-colors duration-200 hover:bg-red-500/10 hover:text-red-300 active:scale-95"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </AppFrame>
  );
}
